import Bookmark from "@svg/new-design/bookmark";
import {
  ICON_COLOR,
  ICON_SIZE,
  ICON_VARIANT,
  IconButton,
} from "../icon-button";
import { useState, MouseEvent, useEffect } from "react";
import BookmarkBold from "@svg/new-design/bookmark-bold";
import {
  PRODUCT_SLUG,
  HOME_EXPERIMENT_LOCATIONS,
  festival,
  DetailsExpire,
  COURSE_TYPE,
} from "@types";
import ImageComponent from "../image";
import classNames from "classnames";
import { modalActions } from "@context/modals";
import { MODAL_TYPES } from "types/modals";
import DiscountBadge from "./discout-badge";
import BadgeCoupon from "./badge-coupon";
import { useLayoutContext } from "@context/layout";
import Label from "../label";
import { CARD_VARIANT, FOLDER_STATUS } from "./types";
import { FAVORITE_TYPE } from "enums/favorite-type";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { useCardAction } from "./useCartAction";
import useHomeExperiments from "@shared/hooks/useHomeExperiments";
import { CardContainerImageClassName, folderIcon } from "./constants";
import { LABEL_COLOR, LABEL_STYLE } from "../label/label";
import { useMemo } from "react";
import { resolveProductImageSources } from "utils/product-image";
import type {
  ApiProductFeaturedImages,
  ApiProductThumbnailImages,
} from "types/api/product-image";

interface Props
  extends ApiProductThumbnailImages,
    Partial<ApiProductFeaturedImages> {
  type: COURSE_TYPE | "landing";
  slug: string;
  sku?: string;
  variant: CARD_VARIANT;
  isFavorite: boolean;
  id: number;
  width?: number;
  sliderSlug?: string;
  title?: string;
  showDiscountIcon?: boolean;
  festival?: festival;
  isList?: boolean;
  changeFavorite?: () => void;
  href?: string;
  showFolderIcon?: boolean;
  folderStatus?: FOLDER_STATUS;
  handleFolderAction?: () => void;
  favoriteType?: FAVORITE_TYPE;
  details_expire?: DetailsExpire;
  showSubscriptionBadge?: boolean;
  renderSubscriptionFooter?: boolean;
}

const CardImage: React.FC<Props> = ({
  svg_image,
  png_image,
  webp_image,
  svg_featured_image,
  png_featured_image,
  webp_featured_image,
  variant,
  isFavorite,
  id,
  sliderSlug,
  title,
  changeFavorite,
  showDiscountIcon,
  festival,
  isList,
  showFolderIcon,
  folderStatus,
  handleFolderAction,
  details_expire,
  showSubscriptionBadge,
  favoriteType = FAVORITE_TYPE.PRODUCT,
  renderSubscriptionFooter = false,
}) => {
  const imageSources = useMemo(
    () =>
      resolveProductImageSources({
        svg_image,
        png_image,
        webp_image,
        svg_featured_image,
        png_featured_image,
        webp_featured_image,
      }),
    [
      svg_image,
      png_image,
      webp_image,
      svg_featured_image,
      png_featured_image,
      webp_featured_image,
    ]
  );

  const { subscriptionData } = useLayoutContext();

  const [favorite, setFavorite] = useState(isFavorite);
  const [isLoading, setIsLoading] = useState(false);

  const isPercentDiscount = festival?.is_percent;
  const discount = festival?.discount_value;

  const isMobile = useIsMobile();
  const { addFavoriteCourse } = useCardAction();
  const homeExperimentSubmitter = useHomeExperiments();

  useEffect(() => {
    setFavorite(isFavorite);
  }, [isFavorite]);

  const handleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (favorite) {
      modalActions.addModal(MODAL_TYPES.REMOVE_CONFIRMATION, {
        config: {
          hideCloseIcon: true,
        },
        data: {
          title: "حذف آموزش از علاقه‌مندی‌های من",
          description: `${title} از فهرست علاقه‌مندی‌های من حذف شود؟`,
          onSubmit: submitFavorite,
          hideCloseIcon: true,
          modalClassName: "rounded-t-24 rounded-b-16",
        },
      });
    } else {
      submitFavorite();
    }
  };

  const submitFavorite = async () => {
    modalActions.clearModals();
    setIsLoading(true);
    const res = await addFavoriteCourse?.(favorite, id, favoriteType);

    homeExperimentSubmitter({
      location: HOME_EXPERIMENT_LOCATIONS.FAVORITE,
      name: sliderSlug as PRODUCT_SLUG,
    });

    setIsLoading(false);
    setFavorite(res);
    changeFavorite?.();
  };

  const hasActiveSubscription = Boolean(subscriptionData?.is_active);
  const hasToShowDiscountIcon = Boolean(
    showDiscountIcon && discount && discount > 0 && !hasActiveSubscription
  );

  const safeFolderStatus =
    (folderStatus as FOLDER_STATUS) || FOLDER_STATUS.FOLDER_REMOVE;
  const Icon = folderIcon[safeFolderStatus].icon;

  const renderFavoriteButton = (
    <div className="absolute right-0 top-0 z-[5] text-fds-secondary">
      {showFolderIcon ? (
        <IconButton
          variant={ICON_VARIANT.DEFAULT}
          color={ICON_COLOR.DEFAULT}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleFolderAction?.();
          }}
          icon={<Icon />}
          size={
            isMobile
              ? variant === CARD_VARIANT.GRID
                ? ICON_SIZE.MEDIUM
                : ICON_SIZE.SMALL
              : ICON_SIZE.LARGE
          }
          disabled={isLoading}
          aria-label={folderIcon[safeFolderStatus].title}
          message={folderIcon[safeFolderStatus].title}
          iconClassName={folderIcon[safeFolderStatus].class}
        />
      ) : (
        <div className="rounded-6 grow shrink-0 relative collectionPicture overflow-hidden group">
          <div className="z-10 rounded-2 absolute right-1 top-1 w-6 h-6 md:top-1.5 md:right-1.5 md:w-7 md:h-7 pointer-events-none bg-white/40 group-has-[button:hover]:bg-white/60" />
          <div className="absolute top-0 right-0 z-10 text-fds-secondary">
            <IconButton
              variant={ICON_VARIANT.DEFAULT}
              color={ICON_COLOR.DEFAULT}
              onClick={handleFavorite}
              icon={favorite ? <BookmarkBold /> : <Bookmark />}
              size={isMobile ? ICON_SIZE.SMALL : ICON_SIZE.MEDIUM}
              disabled={isLoading}
              aria-label={
                favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"
              }
              message={favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"}
              iconClassName={favorite ? "!text-fds-icon-brand" : ""}
              className="hover:!bg-transparent active:!bg-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );

  const hasSubscriptionBadge = Boolean(
    hasActiveSubscription && showSubscriptionBadge
  );
  const shouldRenderSubscriptionBadge = Boolean(
    hasSubscriptionBadge && variant === CARD_VARIANT.GRID
  );

  return (
    <div>
      <div
        className={classNames(
          "relative aspect-video w-full",
          CardContainerImageClassName[variant]
        )}
      >
        <div className="absolute right-0 z-[5] text-fds-secondary">
          {renderFavoriteButton}
        </div>
        {shouldRenderSubscriptionBadge && (
          <Label
            color={LABEL_COLOR.DEFAULT}
            style={LABEL_STYLE.FILL}
            className="rounded-full absolute top-3 left-3 z-10 body-md h-5"
          >
            اشتراک
          </Label>
        )}
        {Boolean(
          hasToShowDiscountIcon &&
            !isList &&
            !shouldRenderSubscriptionBadge &&
            !renderSubscriptionFooter
        ) && (
          <div className="top-1 md:top-1.5 left-1 md:left-1.5 absolute z-[5] h-fds-18 md:h-5">
            <DiscountBadge
              discount={isPercentDiscount ? (discount as number) : "wonder"}
            />
          </div>
        )}
        <div className="w-full aspect-video relative">
          <ImageComponent
            quality={75}
            aspectRatio={9 / 16}
            fill
            alt={title || "course"}
            className="rounded-6 max-w-full overflow-hidden h-full"
            src={imageSources.src}
            fallbackSrcs={imageSources.fallbackSrcs}
          />
        </div>

        {details_expire && <BadgeCoupon details_expire={details_expire} />}
      </div>
      {Boolean(hasToShowDiscountIcon && isList && !hasSubscriptionBadge) && (
        <div className="h-fds-18 md:h-5    mt-1">
          <DiscountBadge
            className="w-[112px] px-1 text-center"
            discount={isPercentDiscount ? (discount as number) : "wonder"}
          />
        </div>
      )}
    </div>
  );
};

export default CardImage;
